import { Sheet, Button, NumberStepper, InlineAlert } from '../../ui';
import { SingerNameField } from './SingerNameField';
import { RateLimitNotice } from './RateLimitNotice';
import { useT } from '../../i18n/i18n.tsx';
import type { RequestErrorCopy } from './requestErrors';
import type { Song } from '../../api/types';

/**
 * DESIGN.md §9.3 [Requests] `RequestSheet` — the mobile `Sheet` (DESIGN.md §10) a tap on a
 * `GuestSongRow` opens: `SingerNameField` (prefilled) plus an optional key change
 * (AC-REQ-4). Purely presentational, the same split as `GuestSearch`/`RequestPage`'s own doc
 * comments describe: `RequestPage` owns `singerName`/`keyShift` state, the `idempotencyKey`
 * lifecycle and the actual `submitRequest()` call; this only renders the two fields and reports
 * one `onSubmit`. `NumberStepper` renders the raw integer (it has no key-formatting slot — see
 * `web/src/ui/NumberStepper.tsx`, not owned by this task) rather than the typographic `+3`/`−2`/
 * `0` DESIGN.md §7 specifies for a key elsewhere (`KeyControl`); fixing that is a `ui/`-level
 * change outside this task's file list.
 */
export function RequestSheet({
  open,
  song,
  singerName,
  onSingerNameChange,
  keyShift,
  onKeyShiftChange,
  submitting,
  errorCopy,
  onDismissError,
  onSubmit,
  onClose,
}: {
  open: boolean;
  song: Song | null;
  singerName: string;
  onSingerNameChange: (value: string) => void;
  keyShift: number;
  onKeyShiftChange: (value: number) => void;
  submitting: boolean;
  errorCopy: RequestErrorCopy | null;
  /** MVP-11 #13: dismissable like every other transient failure — a rate-limit notice's own
   * countdown (`RateLimitNotice`) stays undismissable, it is not a one-off failure. */
  onDismissError?: () => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const canSubmit = singerName.trim().length > 0 && !submitting;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={song?.title ?? t('guest.sheet.titleFallback')}
      closeLabel={t('guest.sheet.close')}
      side="bottom"
      size="lg"
    >
      <form
        className="request-sheet"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit();
        }}
      >
        {song?.artistNameCache && <p className="request-sheet__artist text-meta truncate">{song.artistNameCache}</p>}

        <SingerNameField value={singerName} onChange={onSingerNameChange} autoFocus />

        <div className="request-sheet__key">
          <NumberStepper
            label={t('guest.sheet.keyChangeLabel')}
            value={keyShift}
            onChange={onKeyShiftChange}
            min={-12}
            max={12}
            unit={t('guest.sheet.keyChangeUnit')}
            decreaseLabel={t('guest.sheet.keyChangeDown')}
            increaseLabel={t('guest.sheet.keyChangeUp')}
            disabled={submitting}
            size="lg"
          />
        </div>

        {errorCopy &&
          (errorCopy.retryAfterSec !== undefined ? (
            <RateLimitNotice copy={errorCopy} />
          ) : (
            <InlineAlert variant={errorCopy.tone} onDismiss={onDismissError} dismissLabel={t('common.dismiss')}>
              <strong>{t(errorCopy.titleKey, errorCopy.titleParams)}.</strong> {t(errorCopy.detailKey, errorCopy.detailParams)}
            </InlineAlert>
          ))}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting} disabled={!canSubmit}>
          {t('guest.sheet.submit')}
        </Button>
      </form>
    </Sheet>
  );
}
